import * as React from 'react'
import { useAccountsWithFastboundSubscriptions, type AccountWithSubscriptionType } from './api/get-accounts-with-fastbound-subscriptions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/DataTable'
import { ColumnDef, type PaginationState } from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'

const columns: ColumnDef<AccountWithSubscriptionType>[] = [
    {
        accessorKey: 'name',
        header: 'Account',
        cell: ({ row }) => {
            const account = row.original
            return (
                <Link to={`/accounts/${account.accountId}`} className='text-primary hover:underline flex items-center gap-1'>
                    <Building2 className='h-4 w-4' />
                    {account.name}
                </Link>
            )
        },
    },
    {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <span>{row.getValue('email')}</span>,
    },
    {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ row }) => <span>{row.getValue('phone')}</span>,
    },
    {
        accessorKey: 'city',
        header: 'Location',
        cell: ({ row }) => {
            const city = row.getValue('city') as string | null
            const state = row.original.state
            return <span>{[city, state].filter(Boolean).join(', ') || '—'}</span>
        },
    },
    {
        accessorKey: 'subscriptionCount',
        header: 'Fastbound Subscriptions',
        cell: ({ row }) => {
            const count = row.getValue('subscriptionCount') as number
            return <Badge variant='outline'>{count}</Badge>
        },
    },
    {
        accessorKey: 'isDisabled',
        header: 'Status',
        cell: ({ row }) => {
            const isDisabled = row.getValue('isDisabled') as boolean
            return (
                <Badge variant={isDisabled ? 'destructive' : 'default'}>
                    {isDisabled ? 'Disabled' : 'Active'}
                </Badge>
            )
        },
    },
]

export default function AccountsWithFastboundSubscriptionsReport() {
    const { data: accounts = [], isLoading } = useAccountsWithFastboundSubscriptions({})
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25,
    })

    // Manually paginate the data for client-side pagination
    const paginatedData = React.useMemo(() => {
        const start = pagination.pageIndex * pagination.pageSize
        const end = start + pagination.pageSize
        return accounts.slice(start, end)
    }, [accounts, pagination.pageIndex, pagination.pageSize])

    const handleExport = () => {
        if (!accounts.length) return

        const csvRows = [
            ['Account Name', 'Email', 'Phone', 'City', 'State', 'Fastbound Subscription Count', 'Status'].join(','),
            ...accounts.map(account => [
                `"${account.name.replace(/"/g, '""')}"`,
                `"${account.email.replace(/"/g, '""')}"`,
                `"${account.phone.replace(/"/g, '""')}"`,
                `"${(account.city || '').replace(/"/g, '""')}"`,
                `"${(account.state || '').replace(/"/g, '""')}"`,
                account.subscriptionCount,
                account.isDisabled ? 'Disabled' : 'Active',
            ].join(','))
        ]

        const csvContent = csvRows.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `accounts_with_fastbound_subscriptions_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle>Accounts With Fastbound Subscriptions</CardTitle>
                            <p className='text-sm text-muted-foreground mt-1'>
                                Accounts that have subscriptions with Source = 'fastbound'
                            </p>
                        </div>
                        <Button onClick={handleExport} variant='outline' disabled={isLoading || accounts.length === 0}>
                            <Download className='h-4 w-4 mr-2' />
                            Export CSV
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className='text-sm text-muted-foreground'>Loading accounts...</div>
                    ) : (
                        <>
                            {accounts.length === 0 ? (
                                <div className='text-sm text-muted-foreground'>No accounts with Fastbound subscriptions found.</div>
                            ) : (
                                <>
                                    <div className='mb-4 text-sm text-muted-foreground'>
                                        Found {accounts.length} account{accounts.length !== 1 ? 's' : ''} with Fastbound subscriptions
                                    </div>
                                    <DataTable
                                        columns={columns}
                                        data={paginatedData}
                                        isLoading={isLoading}
                                        enablePagination={true}
                                        serverPagination={true}
                                        pageIndex={pagination.pageIndex}
                                        pageSize={pagination.pageSize}
                                        pageCount={Math.ceil(accounts.length / pagination.pageSize)}
                                        onPaginationChange={setPagination}
                                        serverSorting={false}
                                        scrollContainerClassName='max-h-[calc(100vh-400px)] overflow-y-auto'
                                    />
                                </>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

